import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { formatDate } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Cadence, Role, ServiceDiscipline, ServiceDisciplines } from '../lib/types';

/**
 * Daily Disciplines — the service manager's operating cadence.
 *
 * The Service Delivery page is the scorecard: six numbers, once a month, that
 * say how the desk did. This is the other half of the same question — the
 * habits that produce those numbers, and whether they were kept. A month of a
 * falling kill ratio is a month of skipped triage, and by the time the gauge
 * has moved the day that moved it is four weeks gone.
 *
 * Ticking is per occurrence, not per click: a daily discipline ticked twice on
 * Tuesday is one Tuesday. The occurrence — today, this week, this month — is
 * decided by the server, because the boundary is a business date and a laptop
 * in another timezone would otherwise draw a different day's list than the one
 * it is ticking. See migration 051.
 */

const GROUPS: { cadence: Cadence; label: string; blurb: string }[] = [
  {
    cadence: 'daily',
    label: 'Daily',
    blurb: 'Before the day runs itself. Fifteen minutes at the start, and the queue is yours.',
  },
  {
    cadence: 'weekly',
    label: 'Weekly',
    blurb: 'The numbers that still move while the month is open — and the coaching that moves them.',
  },
  {
    cadence: 'monthly',
    label: 'Monthly',
    blurb: 'Close the month honestly: the scorecard, the misses behind it, and the month ahead.',
  },
];

export function ServiceDisciplinesPage() {
  const resource = useResource<ServiceDisciplines>('/service/disciplines');
  const [editing, setEditing] = useState<ServiceDiscipline | Cadence | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (resource.loading) return <Loading label="Loading the cadence" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  const data = resource.data;
  if (!data) return null;

  /* Optimistic: a tick is one bit, and waiting on a round trip to fill a
     checkbox makes a six-item list feel like a form. The reload afterwards is
     what brings back who ticked it and when. */
  const toggle = (d: ServiceDiscipline) => {
    const done = d.DoneUtc !== null;
    resource.set((current) => ({
      ...current,
      disciplines: current.disciplines.map((row) =>
        row.DisciplineId === d.DisciplineId
          ? { ...row, DoneUtc: done ? null : new Date().toISOString(), DoneBy: null }
          : row,
      ),
    }));
    const call = done
      ? api.del(`/service/disciplines/${d.DisciplineId}/done`)
      : api.post(`/service/disciplines/${d.DisciplineId}/done`);
    call.then(() => resource.reload()).catch((e: Error) => {
      setError(e.message);
      resource.reload();
    });
  };

  const remove = (d: ServiceDiscipline) =>
    api
      .del(`/service/disciplines/${d.DisciplineId}`)
      .then(() => resource.reload())
      .catch((e: Error) => setError(e.message));

  return (
    <>
      <PageHead
        title="Daily Disciplines"
        lede="What the service manager does every day, every week and every month. The scorecard says how the desk performed; this is the cadence that produced it."
        actions={
          <>
            <Link className="btn btn-ghost" to="/service-delivery">
              ← Service Delivery
            </Link>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('daily')}>
              Add a discipline
            </button>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      {data.disciplines.length === 0 ? (
        <Empty title="No disciplines yet">
          <p>
            The standard cadence ships with the app and is re-seeded on deploy, so an empty page
            usually means every one of them was removed. Add the first one back.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => setEditing('daily')}>
            Add a discipline
          </button>
        </Empty>
      ) : (
        GROUPS.map((group) => {
          const rows = data.disciplines.filter((d) => d.Cadence === group.cadence);
          const done = rows.filter((d) => d.DoneUtc !== null).length;

          return (
            <section className="card mb-4" key={group.cadence}>
              <div className="card-head">
                <div className="grow">
                  <span className="eyebrow">{occurrence(group.cadence, data.periods)}</span>
                  <h2>{group.label}</h2>
                  <p className="sub">{group.blurb}</p>
                </div>
                <div className="disc-count">
                  {/* A fraction rather than a percentage, for the reason the CS
                      module states its tooling that way: "4 of 6" says what is
                      left to do, and "67%" does not. */}
                  <span className={done === rows.length && rows.length > 0 ? '' : 'accent'}>
                    {done}
                  </span>
                  <span className="subtle"> / {rows.length}</span>
                  <span className="disc-count-foot">done</span>
                </div>
              </div>

              <div className="card-body">
                {rows.length === 0 ? (
                  <p className="small muted">
                    Nothing {group.label.toLowerCase()} yet.{' '}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setEditing(group.cadence)}
                    >
                      Add one
                    </button>
                  </p>
                ) : (
                  <ul className="disc-list">
                    {rows.map((d) => (
                      <DisciplineRow
                        key={d.DisciplineId}
                        discipline={d}
                        onToggle={() => toggle(d)}
                        onEdit={() => setEditing(d)}
                        onDelete={() => remove(d)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </section>
          );
        })
      )}

      {editing && (
        <DisciplineEditor
          discipline={typeof editing === 'string' ? null : editing}
          cadence={typeof editing === 'string' ? editing : editing.Cadence}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            resource.reload();
          }}
          onError={setError}
        />
      )}
    </>
  );
}

/** What "this occurrence" currently means, said in dates rather than in
 *  "today" — a page left open overnight would otherwise lie about which day
 *  its ticks are landing on. */
function occurrence(cadence: Cadence, periods: ServiceDisciplines['periods']): string {
  const start = periods?.[cadence];
  if (!start) return cadence;
  if (cadence === 'daily') return formatDate(start);
  if (cadence === 'weekly') return `Week of ${formatDate(start)}`;
  const [y, m] = start.slice(0, 10).split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function DisciplineRow({
  discipline: d,
  onToggle,
  onEdit,
  onDelete,
}: {
  discipline: ServiceDiscipline;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const done = d.DoneUtc !== null;

  return (
    <li className={done ? 'disc-row disc-done' : 'disc-row'}>
      <label className="disc-tick">
        <input type="checkbox" checked={done} onChange={onToggle} />
        <span className="grow">
          <span className="disc-title">{d.Title}</span>
          {d.Detail && <span className="disc-detail">{d.Detail}</span>}
          <span className="disc-meta xs subtle">
            {d.OwnerRole ?? 'Unassigned'}
            {/* Who ticked it matters on a shared desk — "done" with no name
                behind it is how a discipline quietly becomes nobody's. */}
            {done && d.DoneBy && <> · ticked by {d.DoneBy}</>}
            {!done && d.LastDoneOn && <> · last done {formatDate(d.LastDoneOn)}</>}
            {!done && !d.LastDoneOn && <> · never recorded</>}
          </span>
        </span>
      </label>
      <span className="disc-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
          Edit
        </button>
        <DeleteButton what={`"${d.Title}"`} label="Remove" onConfirm={onDelete} />
      </span>
    </li>
  );
}

function DisciplineEditor({
  discipline,
  cadence,
  onClose,
  onSaved,
  onError,
}: {
  discipline: ServiceDiscipline | null;
  cadence: Cadence;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const roles = useResource<Role[]>('/roles');
  const [form, setForm] = useState({
    cadence: discipline?.Cadence ?? cadence,
    title: discipline?.Title ?? '',
    detail: discipline?.Detail ?? '',
    ownerRoleId: discipline?.OwnerRoleId === undefined ? null : discipline?.OwnerRoleId ?? null,
    sortOrder: String(discipline?.SortOrder ?? 0),
  });
  const [saving, setSaving] = useState(false);

  const save = () => {
    setSaving(true);
    const body = {
      cadence: form.cadence,
      title: form.title.trim(),
      detail: form.detail.trim() === '' ? null : form.detail.trim(),
      ownerRoleId: form.ownerRoleId,
      sortOrder: Number(form.sortOrder) || 0,
    };
    const call = discipline
      ? api.put(`/service/disciplines/${discipline.DisciplineId}`, body)
      : api.post('/service/disciplines', body);
    call
      .then(onSaved)
      .catch((e: Error) => onError(e.message))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={discipline ? 'Edit discipline' : 'Add a discipline'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || form.title.trim() === ''}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Cadence" hint="How often it is performed.">
          <select
            className="select"
            value={form.cadence}
            onChange={(e) => setForm({ ...form, cadence: e.target.value as Cadence })}
          >
            {GROUPS.map((g) => (
              <option key={g.cadence} value={g.cadence}>
                {g.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Owner" hint="A role, not a person — the cadence outlives the seat.">
          <select
            className="select"
            value={form.ownerRoleId ?? ''}
            onChange={(e) =>
              setForm({ ...form, ownerRoleId: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">Unassigned</option>
            {(roles.data ?? []).map((r) => (
              <option key={r.RoleId} value={r.RoleId}>
                {r.Title}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Discipline" hint="What it is called in the huddle.">
        <input
          className="input"
          value={form.title}
          maxLength={200}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
      </Field>

      <Field
        label="What performing it means"
        hint="The bit that stops two people doing it two different ways."
      >
        <textarea
          className="textarea"
          rows={3}
          maxLength={1000}
          value={form.detail}
          onChange={(e) => setForm({ ...form, detail: e.target.value })}
        />
      </Field>

      <Field label="Order" hint="Lower sorts first within its cadence.">
        <input
          className="input"
          inputMode="numeric"
          value={form.sortOrder}
          onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
        />
      </Field>
    </Modal>
  );
}
